import { browserAnalyticsSettings } from './analytics-browser.mjs';
import { safeGAParameters, returnAnalyticsOptions, safePurchaseParameters, analyticsExplicitlyDenied } from './analytics-parameters.mjs';

const pending = [];
const pendingPurchases = new Map();
const processedPurchases = new Set();

function observePurchase(entry, stage) {
  for (const observer of entry.observers || []) {
    try { observer(stage); } catch { /* Diagnostic failure cannot interrupt analytics. */ }
  }
}

function purchaseDiagnostic(stage, settings, params) {
  if (settings.testEnabled) console.info('[purchase-qa]', stage, {
    transaction_id: params.transaction_id, value: params.value, currency: params.currency,
  });
}

function dispatchPurchase(key, entry, settings) {
  if (entry.dispatched || typeof window.gtag !== 'function') return;
  entry.dispatched = true;
  try {
    window.gtag('event', 'purchase', {
      ...entry.params,
      send_to: settings.tagId,
      ...(settings.testEnabled ? { debug_mode: true } : {}),
      event_callback: () => {
        // SDK processing is not proof of GA receipt. Only this stage may survive reload.
        // The old "1" marker meant merely queued and must never suppress a retry.
        if (analyticsExplicitlyDenied(window, settings.tagId)) return;
        if (processedPurchases.has(key)) return;
        processedPurchases.add(key);
        try { sessionStorage.setItem(key, 'sdk-processed'); } catch { /* Memory fallback. */ }
        pendingPurchases.delete(key);
        purchaseDiagnostic('sdk-processed', settings, entry.params);
        observePurchase(entry, 'sdk_processed');
        for (const callback of entry.callbacks) {
          try { callback(); } catch { /* Analytics never interrupts payment UI. */ }
        }
      },
    });
    observePurchase(entry, 'sdk_dispatched');
    purchaseDiagnostic('dispatched-to-sdk', settings, entry.params);
  } catch (error) {
    entry.dispatched = false;
    throw error;
  }
}

export function flushGAEvents() {
  const settings = browserAnalyticsSettings();
  if (!settings.enabled) { pending.length = 0; pendingPurchases.clear(); return; }
  if (typeof window.gtag !== 'function') return;
  for (const [name, params] of pending.splice(0)) window.gtag('event', name, params);
  if (analyticsExplicitlyDenied(window, settings.tagId)) { pendingPurchases.clear(); lifecyclePending.clear(); return; }
  for (const entry of lifecyclePending.values()) entry.dispatch();
  for (const [key, entry] of pendingPurchases) {
    try { dispatchPurchase(key, entry, settings); } catch { /* Keep queued for a later flush. */ }
  }
}

export const sendGAEvent = (eventName, eventParams = {}) => {
  if (!browserAnalyticsSettings().enabled || eventName === 'purchase') return false;
  if (['login_success', 'invite_code_success'].includes(eventName)) window.dispatchEvent?.(new Event('botgo:authenticated'));
  const params = { ...safeGAParameters(eventParams), ...returnAnalyticsOptions(window.location.href, document.referrer) };
  if (typeof window.gtag === 'function') window.gtag('event', eventName, params);
  else { if (pending.length >= 20) return false; pending.push([eventName, params]); }
  return true;
};

const checkoutTypes = ['subscription_start', 'subscription_upgrade', 'top_up'];
// The server has already created the Stripe page; this marks entry into checkout, not payment.
// Resolves on SDK completion or after waitMs so the caller can redirect without losing the hit.
export function sendBeginCheckout(checkoutType, planKey, waitMs = 800) {
  const settings = browserAnalyticsSettings();
  if (!settings.enabled || analyticsExplicitlyDenied(window, settings.tagId)
      || typeof window.gtag !== 'function' || !checkoutTypes.includes(checkoutType)) return Promise.resolve(false);
  return new Promise(resolve => {
    let settled = false;
    const finish = sent => { if (!settled) { settled = true; resolve(sent); } };
    setTimeout(() => finish(false), waitMs);
    try {
      window.gtag('event', 'begin_checkout', {
        ...safeGAParameters({ plan_key: planKey }), checkout_type: checkoutType,
        items: [{ item_id: checkoutType === 'top_up' ? 'credit_top_up' : 'subscription', quantity: 1 }],
        ...returnAnalyticsOptions(window.location.href, document.referrer),
        send_to: settings.tagId, ...(settings.testEnabled ? { debug_mode: true } : {}),
        event_callback: () => finish(true),
      });
    } catch { finish(false); }
  });
}

// true means accepted/deduplicated, not received by Google. onProcessed is SDK completion.
export function sendVerifiedPurchase(input, onProcessed, onObservation) {
  const settings = browserAnalyticsSettings();
  if (!settings.enabled || analyticsExplicitlyDenied(window, settings.tagId)) return false;
  const params = safePurchaseParameters(input);
  if (!params) return false;
  const key = `ga4-purchase:v2:${settings.tagId}:${params.transaction_id}`;
  let processed = processedPurchases.has(key);
  try { processed ||= sessionStorage.getItem(key) === 'sdk-processed'; } catch { /* Memory fallback. */ }
  if (processed) {
    purchaseDiagnostic('already-sdk-processed', settings, params);
    try { onObservation?.('already_sdk_processed'); } catch { /* Optional diagnostics. */ }
    onProcessed?.();
    return true;
  }
  let entry = pendingPurchases.get(key);
  if (!entry) {
    if (pendingPurchases.size >= 20) return false;
    entry = { params: { ...params, ...returnAnalyticsOptions(window.location.href, document.referrer) },
      dispatched: false, callbacks: new Set(), observers: new Set() };
    pendingPurchases.set(key, entry);
    purchaseDiagnostic('queued-in-memory', settings, params);
  }
  if (onObservation) {
    entry.observers.add(onObservation);
    if (entry.dispatched) {
      try { onObservation('shared_sdk_dispatch'); } catch { /* Optional diagnostics. */ }
    }
  }
  if (onProcessed) entry.callbacks.add(onProcessed);
  try { dispatchPurchase(key, entry, settings); return true; } catch { return false; }
}

const lifecyclePending = new Map();
const lifecycleProcessed = new Set();
// The caller supplies an authoritative, server-verified business event key.
export function sendVerifiedLifecycleEvent(name, params, eventKey, onProcessed) {
  const settings = browserAnalyticsSettings();
  if (!settings.enabled || analyticsExplicitlyDenied(window, settings.tagId)
      || !['sign_up', 'first_generation', 'subscription_success'].includes(name)) return false;
  const key = `ga4-lifecycle:v1:${settings.tagId}:${name}:${eventKey}`;
  if (lifecycleProcessed.has(key)) { onProcessed?.(); return true; }
  try { if (localStorage.getItem(key) === 'sdk-processed') { onProcessed?.(); return true; } } catch { /* Memory fallback. */ }
  if (lifecyclePending.has(key)) return true;
  if (lifecyclePending.size >= 20) return false;
  const entry = { name, params, onProcessed, dispatched: false };
  lifecyclePending.set(key, entry);
  const dispatch = () => {
    if (entry.dispatched || typeof window.gtag !== 'function') return;
    entry.dispatched = true;
    try { window.gtag('event', name, { ...safeGAParameters(params), ...returnAnalyticsOptions(window.location.href, document.referrer),
      send_to: settings.tagId, ...(settings.testEnabled ? { debug_mode: true } : {}),
      event_callback: () => {
        if (analyticsExplicitlyDenied(window, settings.tagId) || lifecycleProcessed.has(key)) return;
        lifecycleProcessed.add(key);
        try { localStorage.setItem(key, 'sdk-processed'); } catch { /* Optional storage. */ }
        lifecyclePending.delete(key);
        onProcessed?.();
      },
    }); } catch { entry.dispatched = false; }
  };
  entry.dispatch = dispatch;
  dispatch();
  return true;
}
